// src/modules/bookLocation/keyHandover/pages/newSubscription/SubscriptionSummaryPage.tsx

import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Trans, useTranslation } from "react-i18next";
import type { RootState } from "../../../../../redux/store";
import { useAppDispatch } from "../../../../../redux/useDispatch";
import { setSelectedSubscriptionPlan } from "../../../../../redux/slice/subscription/subscriptionSlice";
import { useGetCommercialModelsByLocationQuery } from "../../../../../api/commercialModels/commercialModelsApiSlice";
import { KeyHandoverPageLayout } from "../../components/KeyHandoverPageLayout";
import { KeyHandoverContentWrapper } from "../../components/KeyHandoverContentWrapper";
import { Button } from "../../../../../components/ui/button";
import { Spinner } from "../../../../../components/ui/spinner";
import { Checkbox } from "../../../../../components/ui/checkbox";
import HintsDialog from "../../../../hints/components/HintsDialog";
import WithdrawalInformation from "../../../../hints/components/WithdrawalInformation";
import {
    useInPlaceTerms,
    useInPlaceWithdrawal,
} from "../../../common/hooks/useTenantTermsPage";
import { useDataProtectionPath } from "../../../common/hooks/useDataProtectionPath";
import { buildAppUrl } from "../../../../../util";
import routes from "../../../../../routes/routes";
import { useLocationHydration } from "../../hooks/useLocationHydration";
import { useSubscriptionHydration } from "../../hooks/useSubscriptionHydration";
import { useSubscriptionUrlSync } from "../../hooks/useSubscriptionUrlSync";
import { parseSubscriptionQuery } from "../../common/url/subscriptionQuery";
import { isEmailAcceptable } from "../../../../../util/validation";
import SubscriptionSummaryStep from "../../steps/KeyHandoverSubscriptionSummaryStep";

/** A link `<Trans>` fills with its text; opens in a new tab. */
const NewTabLink = ({ href, children }: { href: string; children?: React.ReactNode }) => (
    <a href={href} target="_blank" rel="noreferrer" className="underline">
        {children}
    </a>
);

interface SubscriptionLegalConsentProps {
    earlyStartRequested: boolean;
    onEarlyStartRequestedChange: (requested: boolean) => void;
}

/**
 * What a consumer must see and declare before the subscription is concluded
 * (plan `docs/plans/kolula-key-subscription-b2c-obligations.md`, PR 1): the
 * AGB, the withdrawal information, and the express request that the service
 * starts before the withdrawal period ends (§ 357a(2) BGB). The subscription
 * starts on payment, so without that request it could not start at all.
 *
 * Both documents open IN PLACE, held open by a search param, so reading them
 * neither leaves the flow nor loses the plan and location in the URL.
 */
const SubscriptionLegalConsent = ({
    earlyStartRequested,
    onEarlyStartRequestedChange,
}: SubscriptionLegalConsentProps) => {
    const { t, i18n } = useTranslation();
    const { TermsPage, termsOpen, openTerms, closeTerms } = useInPlaceTerms();
    const { withdrawalOpen, openWithdrawal, closeWithdrawal } = useInPlaceWithdrawal();
    const termsPath = useDataProtectionPath();

    return (
        <div className="flex flex-col gap-3 text-sm text-foreground/80">
            <p>
                <Trans
                    i18nKey="book_location.keys.subscription_summary.terms"
                    components={{
                        termsLink: TermsPage ? (
                            <button
                                type="button"
                                onClick={openTerms}
                                className="underline text-left"
                            />
                        ) : (
                            // Legacy tenants have no component-based terms page;
                            // their hint pages come from the backend list. A new
                            // tab keeps this step — and the request below — open.
                            <NewTabLink href={buildAppUrl(termsPath)} />
                        ),
                    }}
                />
            </p>
            <p>
                <button type="button" onClick={openWithdrawal} className="underline text-left">
                    {t("book_location.keys.subscription_summary.withdrawal_link")}
                </button>
            </p>
            <label className="flex items-start gap-2 cursor-pointer">
                <Checkbox
                    className="mt-0.5"
                    checked={earlyStartRequested}
                    onCheckedChange={(checked) => onEarlyStartRequestedChange(checked === true)}
                    aria-required="true"
                />
                <span>
                    {t("book_location.keys.subscription_summary.early_start_request")}
                </span>
            </label>

            {TermsPage && (
                <HintsDialog open={termsOpen} onClose={closeTerms} showFaqsBack={false}>
                    <TermsPage lang={i18n.language} />
                </HintsDialog>
            )}
            <HintsDialog open={withdrawalOpen} onClose={closeWithdrawal} showFaqsBack={false}>
                <WithdrawalInformation lang={i18n.language} />
            </HintsDialog>
        </div>
    );
};

function SummaryContent({
    earlyStartRequested,
    onEarlyStartRequestedChange,
}: SubscriptionLegalConsentProps) {
    const { t } = useTranslation();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const hydratedRef = React.useRef(false);
    const planHydratedRef = React.useRef(false);
    const [bothHydrated, setBothHydrated] = React.useState(false);

    const locationHydrated = useLocationHydration();
    const subscriptionHydrated = useSubscriptionHydration();

    const selectedLocation = useSelector(
        (state: RootState) => state.subscription.newSubscription.selectedLocation
    );
    const selectedPlan = useSelector(
        (state: RootState) => state.subscription.newSubscription.selectedSubscriptionPlan
    );

    // Fetch plans for selected location
    const { data: subscriptionPlans, isLoading: plansLoading } = useGetCommercialModelsByLocationQuery(
        selectedLocation?.id || "",
        {
            skip: !selectedLocation?.id,
        }
    );

    // Debug logging
    React.useEffect(() => {
    }, [locationHydrated, subscriptionHydrated, bothHydrated, selectedLocation, selectedPlan, plansLoading, subscriptionPlans]);

    // Hydrate selected plan from URL if needed
    React.useEffect(() => {
        if (planHydratedRef.current) return;
        if (!subscriptionPlans || subscriptionPlans.length === 0) {
            return;
        }

        const query = parseSubscriptionQuery(location.search);
        const planId = query.selectedSubscriptionPlanId;

        if (planId && (!selectedPlan || selectedPlan.id !== planId)) {
            const found = subscriptionPlans.find((p) => p.id === planId);
            if (found) {
                dispatch(setSelectedSubscriptionPlan(found));
            } else {
                console.warn('[SummaryContent] Plan from URL not found:', planId);
            }
        }

        planHydratedRef.current = true;
    }, [subscriptionPlans, selectedPlan, location.search, dispatch, plansLoading]);

    const isPlanHydrated = planHydratedRef.current;

    // Mark as hydrated after all hydrations complete
    React.useEffect(() => {
        if (locationHydrated && subscriptionHydrated && isPlanHydrated && !bothHydrated) {
            setBothHydrated(true);
            hydratedRef.current = true;
        }
    }, [locationHydrated, subscriptionHydrated, isPlanHydrated, bothHydrated]);

    // Sync subscription state back to URL
    useSubscriptionUrlSync(hydratedRef);

    if (!bothHydrated) {
        return (
            <div className="flex justify-center items-center py-12">
                <Spinner />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <p
                className="-mt-5 text-xs text-muted-foreground text-center"
                role="status"
                aria-live="polite"
            >
                {t("book_location.keys.step_counter", {
                    current: 3,
                    total: 3,
                    defaultValue: "Schritt {{current}} von {{total}}",
                })}
            </p>
            <SubscriptionSummaryStep />
            <SubscriptionLegalConsent
                earlyStartRequested={earlyStartRequested}
                onEarlyStartRequestedChange={onEarlyStartRequestedChange}
            />
        </div>
    );
}

const SubscriptionSummaryPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const selectedPlan = useSelector(
        (state: RootState) => state.subscription.newSubscription.selectedSubscriptionPlan
    );
    const hostData = useSelector((state: RootState) => state.subscription.newSubscription.hostData);
    // Local on purpose: the request is declared on this screen, every time.
    // Proceeding navigates to the payment route, which unmounts this page, so
    // coming back — from the payment step or from Stripe — finds it unticked.
    const [earlyStartRequested, setEarlyStartRequested] = React.useState(false);

    const isValid = Boolean(
            earlyStartRequested &&
            selectedPlan &&
            hostData?.firstName &&
            hostData?.lastName &&
            hostData?.email &&
            isEmailAcceptable(hostData.email) &&
            hostData?.phone
    );

    const handleProceedToPayment = () => {
        navigate({
            pathname: routes.keyHandoverSubscriptionPaymentPath,
            search: location.search,
        });
    };

    return (
        <KeyHandoverPageLayout
            headerText={t("book_location.keys.subscription_summary.header_text", {
                defaultValue: "Zusammenfassung",
            })}
            backTo={routes.keyHandoverSubscriptionHostDataPath}
            bottomActions={
                <div className="pt-4">
                    <Button
                        onClick={handleProceedToPayment}
                        className="w-full"
                        size="lg"
                        disabled={!isValid}
                    >
                        {t("book_location.keys.subscription_summary.proceed_to_payment", {
                            defaultValue: "Proceed to Checkout",
                        })}
                    </Button>
                </div>
            }
        >
            <KeyHandoverContentWrapper>
                <SummaryContent
                    earlyStartRequested={earlyStartRequested}
                    onEarlyStartRequestedChange={setEarlyStartRequested}
                />
            </KeyHandoverContentWrapper>
        </KeyHandoverPageLayout>
    );
};

export default SubscriptionSummaryPage;
